import { useEffect, useState } from 'react';
import {
  normalizeAuthPlacement,
  type TattooBookingAuthPlacement,
} from '../utils/tattooBookingSteps';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const CACHE_TTL_MS = 60_000;

export interface SystemStatus {
  isTestMode: boolean;
  onlinePaymentProvider: 'STRIPE' | 'VIVA';
  tattooBookingEnabled: boolean;
  tattooBookingAuthPlacement: TattooBookingAuthPlacement;
  frontoffWorkingMode: 'full' | 'limited';
  piercingBookingZoneGuideEnabled: boolean;
  piercingBookingAnonymousEnabled: boolean;
}

const DEFAULT_STATUS: SystemStatus = {
  isTestMode: false,
  onlinePaymentProvider: 'STRIPE',
  tattooBookingEnabled: true,
  tattooBookingAuthPlacement: 'before_summary',
  frontoffWorkingMode: 'full',
  piercingBookingZoneGuideEnabled: false,
  piercingBookingAnonymousEnabled: false,
};

let cachedStatus: SystemStatus | null = null;
let cachedAt = 0;
let fetchPromise: Promise<SystemStatus> | null = null;

async function fetchSystemStatus(): Promise<SystemStatus> {
  const now = Date.now();
  if (cachedStatus !== null && now - cachedAt < CACHE_TTL_MS) {
    return cachedStatus;
  }
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(`${API_BASE_URL}/api/system/status`)
    .then((r) => r.json())
    .then((d) => {
      const data = d?.data ?? {};
      cachedStatus = {
        isTestMode: data.isTestMode === true,
        onlinePaymentProvider: data.onlinePaymentProvider === 'VIVA' ? 'VIVA' : 'STRIPE',
        tattooBookingEnabled: data.tattooBookingEnabled !== false,
        tattooBookingAuthPlacement: normalizeAuthPlacement(data.tattooBookingAuthPlacement),
        frontoffWorkingMode: data.frontoffWorkingMode === 'limited' ? 'limited' : 'full',
        piercingBookingZoneGuideEnabled: data.piercingBookingZoneGuideEnabled === true,
        piercingBookingAnonymousEnabled: data.piercingBookingAnonymousEnabled === true,
      };
      cachedAt = Date.now();
      fetchPromise = null;
      return cachedStatus!;
    })
    .catch(() => {
      fetchPromise = null;
      return cachedStatus ?? DEFAULT_STATUS;
    });

  return fetchPromise;
}

/**
 * Public system flags for the frontoffice (test mode, payment provider, booking auth placement).
 * Cached for 1 minute at module level.
 */
export function useSystemStatus() {
  const [status, setStatus] = useState<SystemStatus>(cachedStatus ?? DEFAULT_STATUS);
  const [loading, setLoading] = useState(cachedStatus === null);

  useEffect(() => {
    fetchSystemStatus().then((value) => {
      setStatus(value);
      setLoading(false);
    });
  }, []);

  return { ...status, loading };
}

/** Backward-compatible wrapper used by existing pages. */
export function useTestMode() {
  const { isTestMode, loading } = useSystemStatus();
  return { isTestMode, loading };
}

export function useFrontoffWorkingMode() {
  const { frontoffWorkingMode, loading } = useSystemStatus();
  const isLimited = frontoffWorkingMode === 'limited';

  return {
    isLimited,
    loading,
    features: {
      languageSwitch: !isLimited,
      userGuide: !isLimited,
      boutique: !isLimited,
      becomePartner: !isLimited,
      homepageExtraCtas: !isLimited,
    },
  };
}
